import React, { useEffect, useState } from 'react';
import topApi from '../../components/api/ToPApi';
import { errorMessage } from '../../utils/access';
import { downloadCsv } from '../../utils/csv';
import { featureIcon, formatMoney, formatTotals, MonthlyTotal } from './featureUi';

interface BillingLine {
    code: string;
    name: string;
    merchants: number;          // comercios que generan el cargo
    quantity: number;           // unidades cobradas: licencias, bloques, comercios
    unit: string;               // licencia, bloque, comercio, fee
    unitCost: number | null;
    subtotal: number;
    detail: string | null;
}

interface BillingBank {
    bankId: number;
    bankName: string;
    currency: string;
    merchants: number;          // comercios activos del banco en el mes
    configured: boolean;        // tiene licenciamiento
    tier: { from: number; to: number | null; basePrice: number } | null;
    lines: BillingLine[];
    total: number;
    missingCost: boolean;
}

interface BillingResponse { banks: BillingBank[]; totals: MonthlyTotal[]; period: string; generatedAt: string; }

const LINE_ICON: Record<string, string> = { 'platform-fee': 'bi-building' };

const UNIT_LABEL: Record<string, string> = { fee: 'fee', licencia: 'licencias', bloque: 'bloques', comercio: 'comercios' };

const currentPeriod = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const periodLabel = (period: string) => {
    const [y, m] = period.split('-').map(Number);
    return y && m ? new Date(y, m - 1, 1).toLocaleDateString('es-CR', { month: 'long', year: 'numeric' }) : period;
};

/**
 * Cobro por banco (plataforma): lo que se le cobra a cada banco en el mes según su licenciamiento, en dólares.
 *   fee de plataforma + licencias Base que exceden las incluidas × precio del tramo (según los comercios activos)
 *   + módulos por comercio que los usa: facturación electrónica y contabilidad por bloques de documentos emitidos
 *   y facturas leídas. No usa el precio que el banco le cobra al comercio.
 */
export const BankBillingPage = () => {
    const [banks, setBanks] = useState<{ id: number; name: string }[]>([]);
    const [bankId, setBankId] = useState('');
    const [period, setPeriod] = useState(currentPeriod());
    const [data, setData] = useState<BillingResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        topApi.get<{ banks: { id: number; name: string }[] }>('/bank/getBanks')
            .then(resp => setBanks((resp.data.banks ?? []).map(b => ({ id: b.id, name: b.name }))))
            .catch(() => undefined);
    }, []);

    useEffect(() => {
        if (!/^\d{4}-\d{2}$/.test(period)) return;
        setLoading(true);
        setError('');
        topApi.get<BillingResponse>('/features/billing', { params: { bankId: bankId || undefined, period } })
            .then(resp => setData(resp.data))
            .catch(e => { setError(errorMessage(e)); setData(null); })
            .finally(() => setLoading(false));
    }, [bankId, period]);

    const exportCsv = () => {
        if (!data) return;
        downloadCsv(`cobro-por-banco-${data.period}.csv`, data.banks.flatMap(bank => bank.lines.map(line => ({
            Periodo: data.period,
            Banco: bank.bankName,
            'Comercios activos': bank.merchants,
            Concepto: line.name,
            Comercios: line.merchants,
            Cantidad: line.quantity,
            Unidad: line.unit,
            'Precio unitario (USD)': line.unitCost ?? '',
            'Subtotal (USD)': line.subtotal,
            Detalle: line.detail ?? '',
        }))));
    };

    const totalMerchants = data?.banks.reduce((sum, b) => sum + b.merchants, 0) ?? 0;
    const usage = (code: string) => data?.banks.flatMap(b => b.lines).filter(l => l.code === code) ?? [];

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-4">
                <div>
                    <h3 className="mb-1">Cobro por banco</h3>
                    <p className="text-muted mb-0">Monto mensual a cobrar a cada banco según su licenciamiento: fee de plataforma, licencias Base por tramo y módulos por uso (en dólares).</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <input type="month" className="form-control" style={{ maxWidth: 180 }} aria-label="Mes" value={period}
                        max={currentPeriod()} onChange={e => setPeriod(e.target.value)} />
                    <select className="form-select" style={{ maxWidth: 260 }} aria-label="Banco" value={bankId} onChange={e => setBankId(e.target.value)}>
                        <option value="">Todos los bancos</option>
                        {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    <button type="button" className="btn btn-outline-secondary text-nowrap" disabled={!data?.banks.length} onClick={exportCsv}>
                        <i className="bi bi-download me-1" /> CSV
                    </button>
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {loading ? (
                <div className="text-center py-5"><span className="spinner-border text-primary" /></div>
            ) : data && (<>
                {/* TOTALES */}
                <div className="row g-3 mb-4">
                    <div className="col-sm-6 col-lg-3">
                        <div className="card border-0 shadow-sm h-100"><div className="card-body">
                            <div className="text-muted small">Total a cobrar · {periodLabel(data.period)}</div>
                            <div className="fs-4 fw-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{data.totals.length ? formatTotals(data.totals) : '-'}</div>
                        </div></div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card border-0 shadow-sm h-100"><div className="card-body">
                            <div className="text-muted small">Comercios activos</div>
                            <div className="fs-4 fw-semibold">{totalMerchants.toLocaleString('es-CR')}</div>
                            <div className="small text-muted">{data.banks.length} banco(s)</div>
                        </div></div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card border-0 shadow-sm h-100"><div className="card-body">
                            <div className="text-muted small">Facturación electrónica</div>
                            <div className="fs-4 fw-semibold">{usage('electronic-invoice').reduce((sum, l) => sum + l.quantity, 0).toLocaleString('es-CR')} bloques</div>
                            <div className="small text-muted">{usage('electronic-invoice').reduce((sum, l) => sum + l.merchants, 0)} comercio(s) con el módulo</div>
                        </div></div>
                    </div>
                    <div className="col-sm-6 col-lg-3">
                        <div className="card border-0 shadow-sm h-100"><div className="card-body">
                            <div className="text-muted small">Contabilidad</div>
                            <div className="fs-4 fw-semibold">{usage('accounting').reduce((sum, l) => sum + l.quantity, 0).toLocaleString('es-CR')} bloques</div>
                            <div className="small text-muted">{usage('accounting').reduce((sum, l) => sum + l.merchants, 0)} comercio(s) con el módulo</div>
                        </div></div>
                    </div>
                </div>

                {data.banks.length === 0 && (
                    <div className="card border-0 shadow-sm"><div className="card-body text-muted text-center py-5">No hay comercios activos en el mes.</div></div>
                )}

                {data.banks.map(bank => (
                    <div key={bank.bankId} className="card border-0 shadow-sm mb-3">
                        <div className="card-body">
                            <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-2">
                                <h5 className="mb-0">{bank.bankName}</h5>
                                <span className="text-muted small">
                                    {bank.merchants.toLocaleString('es-CR')} comercio(s) activo(s)
                                    {bank.tier && <> · tramo {bank.tier.from.toLocaleString('es-CR')}{bank.tier.to === null ? ' o más' : ` – ${bank.tier.to.toLocaleString('es-CR')}`} ({formatMoney(bank.tier.basePrice, 'USD')} por licencia Base)</>}
                                </span>
                            </div>
                            {bank.missingCost && (
                                <div className="alert alert-warning small py-2">
                                    <i className="bi bi-exclamation-triangle me-1" />
                                    {bank.configured
                                        ? 'Hay cargos sin precio definido para este banco; no suman al total. Defínalos en Funcionalidades.'
                                        : 'Este banco no tiene licenciamiento configurado; la licencia Base no suma al total. Configúrelo en Funcionalidades.'}
                                </div>
                            )}
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead>
                                        <tr className="text-muted small text-uppercase">
                                            <th>Concepto</th>
                                            <th className="text-end">Comercios</th>
                                            <th className="text-end">Cantidad</th>
                                            <th className="text-end">Precio unitario</th>
                                            <th className="text-end">Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {bank.lines.map(line => (
                                            <tr key={line.code}>
                                                <td>
                                                    <i className={`bi ${LINE_ICON[line.code] ?? featureIcon(line.code)} me-2 text-primary`} />{line.name}
                                                    {line.detail && <div className="small text-muted ms-4">{line.detail}</div>}
                                                </td>
                                                <td className="text-end">{line.merchants.toLocaleString('es-CR')}</td>
                                                <td className="text-end">{line.unit === 'fee' ? '1' : `${line.quantity.toLocaleString('es-CR')} ${UNIT_LABEL[line.unit] ?? line.unit}`}</td>
                                                <td className="text-end">{line.unitCost === null ? <span className="text-warning-emphasis">Sin definir</span> : formatMoney(line.unitCost, bank.currency)}</td>
                                                <td className="text-end">{formatMoney(line.subtotal, bank.currency)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <th colSpan={4} className="text-end">Total {bank.bankName}</th>
                                            <th className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMoney(bank.total, bank.currency)}</th>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                ))}
                <p className="small text-muted">
                    Generado {new Date(data.generatedAt).toLocaleString('es-CR')}. Comercios activos: con el servicio vigente en algún día del mes.
                    Documentos emitidos de facturación electrónica y facturas leídas por contabilidad del mes. Los comercios sin banco no se cobran a ningún banco.
                </p>
            </>)}
        </div>
    );
};
